<script setup>
import moment from "moment";
import axios from "axios";
import "vue-select/dist/vue-select.css";
import createProject from "@/components/home/reporting/dataAnalyser/createProject.vue";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import {justDate} from "@/helpers/dateTimeHelpers";
import useDatePicker from "@/components/shared/datePicker/useDatePicker";
import useMapImagery from "@/components/maps/shared/composables/useMapImagery.js";
import { useReportingMapFilter } from "@/composables/useReportingMapFilter";
import AddressSearch from "@/components/maps/shared/AddressSearch.vue";
import WKT from "ol/format/WKT";
import TableRowDataLoader from "@/components/shared/loader/TableRowDataLoader.vue";
import { create, over } from "lodash";
import Tooltip from "@/components/shared/tooltip/Tooltip.vue";
import FtDatePicker from "../../../shared/datePicker/FtDatePicker.vue";

const props = defineProps(["suburbs", "permissions", "attachments", "zones"]);

const { textInputObject, handleRangeData } = useDatePicker();
const showPostponeDates = ref(false);
const postponeMsg = ref(null);
const postponeDays = ref(30);
const postponeFromDate = ref(moment().format("YYYY-MM-DD"));
const postponeFromDateEnabled = ref(false);
const postponeLoading = ref(false);
const loading = ref(true);
const offset = ref(3);
const pagination = ref({});
const showProject = ref(false);
const next_inspect_between = ref(null);
const openActions = ref(false);
const filter = ref({
  trees_status_id: [2],
  trees_species_id: null,
  trees_risk_id: null,
  park_tree: null,
  planted_by: null,
  suburb: null,
  overdue_only: false,
});
const project = ref({
  name: "",
  notes: "",
  workTasks: [],
  totalTrees: 0,
  typeSelected: 7,
  polygonArea: "",
  canInspect: false,
});

// Map related states and composables
const showMap = ref(false);
const isAreaMenuOpen = ref(false);
const trees = ref([]);
const areaLoading = ref(false);

const {
  map,
  createMap,
  polygonWkt,
  updateWkt,
  addTreesGL,
  addFeatureToMap,
  enablePolygonDraw,
  filterTreesSource,
  polygonSource,
  osm,
  imageService,
  aerialGroup,
  labels,
  satellite,
  aerial,
  restart,
} = useReportingMapFilter();

const { baseMapSwitch, addAdditionalLayers } = useMapImagery({
  map,
  osmLayer: osm,
  imageService,
  aerialGroup,
  labelLayer: labels,
  satelliteLayer: satellite,
  aerialLayer: aerial, // <-- fix here
});

const clearArea = () => {
  if (polygonSource.value) {
    restart();
  }
};

const toggleShowAreaMenu = () => {
    isAreaMenuOpen.value = !isAreaMenuOpen.value;
};

const handleSelectArea = (area) => {
  addFeatureToMap(area);
  isAreaMenuOpen.value = false;
};

const pages = computed(() => {
  if (!pagination.value.to) {
    return null;
  }
  let from = pagination.value.current_page - offset.value;
  if (from < 1) {
    from = 1;
  }
  let to = from + offset.value * 2;
  if (to >= pagination.value.last_page) {
    to = pagination.value.last_page;
  }
  let pages = [];
  for (let page = from; page <= to; page++) {
    pages.push(page);
  }
  return pages;
});
onMounted( async () => {
  await createMap(true, 'risk');
  addAdditionalLayers();
  getScheduledInspections(true);

  document.addEventListener("click", close.value);
});

onUnmounted(() => {
  document.removeEventListener("click", close.value);
});

const openActionMenu = () => {
  openActions.value = !openActions.value;
};

const close = (e) => {
  if (this.$refs.actions && !this.$refs.actions.contains(e.target)) {
    openActions.value = false;
  }
};

const applyFilter = () => {
    if (loading.value === true) {
        return;
    }
    getScheduledInspections();
}
const getScheduledInspections = (noMap=false) => {
  loading.value = true;
  axios
    .post("/api/insepctions/upcoming?" + queryString(), {
      area: polygonWkt.value,
    })
    .then((response) => {
      pagination.value = response.data.trees;
      trees.value = response.data.map_trees || [];
      loading.value = false;
      if (filterTreesSource.value) {
          if (!noMap) {
              addTreesGL(trees.value);
          }
      } else {
          createMap(true, 'risk');
      }
    })
    .catch((error) => {
      alert(error);
    });
};


const updatesInspections = () => {
  var r = confirm(
    "Are you sure you want to postpone these Inspection Dates? This cannot be undone."
  );
  if (r == true) {
    postponeLoading.value = true;
    axios
      .get(
        `/api/insepctions/update-inspections?days=${
          postponeDays.value
        }&customDate=${postponeFromDate.value}&customDateEnable=${
          postponeFromDateEnabled.value
        }&${queryString()}`
      )
      .then((response) => {
        getScheduledInspections(true);
        postponeLoading.value = false;
        showPostponeDates.value = false;
      })
      .catch((error) => {
        postponeMsg.value = error;
        postponeLoading.value = false;
      });
  }
};
const clearFilter = () => {
  if (loading.value === true) {
      return;
  }
  filter.value = {
    trees_status_id: [2],
    trees_species_id: null,
    trees_risk_id: null,
    park_tree: null,
    planted_by: null,
  };

  next_inspect_between.value = null;
  getScheduledInspections();
};
const closeProject = () => {
  showProject.value = false;
  project.value = {
    name: "",
    notes: "",
    workTasks: [],
    totalTrees: 0,
    typeSelected: 7,
    polygonArea: "",
    canInspect: false,
  };
};
const saveProject = () => {
    var polygon = "";
    if (polygonWkt.value) {
        var format = new WKT();
        var feature = format.readFeature(polygonWkt.value, {
            dataProjection: "EPSG:3857",
            featureProjection: "EPSG:4326",
        });
        polygon = format.writeGeometry(feature.getGeometry());
    }
  axios
    .post("/api/insepctions/projects/new?num_records=10000&" + queryString(), {
      area: polygonWkt.value,
      project: {
        name: project.value.name,
        workTasks: project.value.workTasks,
        notes: project.value.notes,
        totalTrees: pagination.value.total,
        treesToPlant: 0,
        typeSelected: project.value.typeSelected,
        polygonArea: polygon,
        canInspect: project.value.canInspect,
        filterString: queryString(),
        type: "scheduled",
      },
    })
    .then((response) => {
      window.location.href = "/projects";
    })
    .catch((error) => {
      alert(error);
    });
};
const queryString = (getMapData = true) => {
  var q = "";

  let keys = Object.keys(filter.value);
  for (let key of keys) {
    if (filter.value[key] != null && filter.value[key] !== "") {
      	var val = "";
		if (Array.isArray(filter.value[key])) {
			if (filter.value[key].length > 0) {
				filter.value[key].forEach((e, i) => {
					if (i !== 0) {
						val = val + ",";
					}
					val = val + e;
				});
				if (key !== "trees_status_id") {
					q = q + "filter[" + key + "]=" + val + "&";
				}
			}
		} else {
			q = q + "filter[" + key + "]=" + filter.value[key] + "&";
		}
      
    }
  }

  if (next_inspect_between.value) {
    q =
      q +
      "filter[next_inspect_between]=" +
      moment(next_inspect_between.value.start).format("YYYY-MM-DD") +
      "," +
      moment(next_inspect_between.value.end).format("YYYY-MM-DD") +
      "&";
  }

  return q;
};

const overdue = (dte) => {
  return moment(dte) < moment();
};

const changePage = (page) => {
  if (loading.value) {
    return;
  }
  loading.value = true;
  axios
    .post("/api/insepctions/upcoming?" + queryString() + "&page=" + page, {
      area: polygonWkt.value,
    })
    .then((response) => {
      pagination.value = response.data.trees;
      loading.value = false;
    })
    .catch((error) => {
      console.log(error);
    });
};
const changePageUrl = (url) => {
  if (loading.value) {
    return;
  }
  loading.value = true;
  axios
    .post(url + "&" + queryString(), {
      area: polygonWkt.value,
    })
    .then((response) => {
      pagination.value = response.data.trees;
      loading.value = false;
    })
    .catch((error) => {
      console.log(error);
    });
};
const getStatus = (dte) => {
  if (moment(dte) < moment()) {
    return "Overdue";
  } else {
    var startDate = moment();
    var endDate = moment(dte);
    var diff = endDate.diff(startDate, "days");
    if (diff > 365) {
      return "Due in " + endDate.diff(startDate, "years") + " year(s)";
    } else if (diff > 31) {
      return "Due in " + endDate.diff(startDate, "months") + " month(s)";
    } else {
      return "Due in " + diff + " day(s)";
    }
  }
};

const handleDateBetween =(value) =>{
    return handleRangeData(value, next_inspect_between)
}
</script>

<template>
  <div>
    <div class="flex">
      <div class="lg:w-1/2">
        <p class="my-3 text-xl text-slate-600">Upcoming Inspections Due</p>
        <p class="my-3 text-md text-slate-500">
          Scheduled Inspections are generated based on the risk of the tree, or
          can be individually customised for each tree.
        </p>
      </div>
      <div class="text-right lg:w-1/2 text-slate-600"></div>
    </div>

    <div class="w-full">
      <div class="flex flex-wrap w-full text-sm lg:flex-no-wrap">
        <div class="w-full p-2 lg:w-2/6">
          <p class="text-sm text-slate-600">Next Inspection Date Range</p>
            <FtDatePicker
              v-model="next_inspect_between"
              :modelValue="next_inspect_between"
              @update:modelValue="handleDateBetween"
            />
            <fieldset class="pt-1 mt-4 space-y-5">
                <div class="relative flex items-start">
                    <div class="flex items-center h-5">
                        <input
                            v-model="filter.overdue_only"
                            id="overdue_only"
                            aria-describedby="overdue_only-description"
                            name="overdue_only"
                            type="checkbox"
                            class="w-4 h-4 mt-1 text-indigo-600 rounded focus:ring-indigo-500 border-slate-300"
                        />
                    </div>
                    <div class="ml-3 text-sm">
                        <label for="overdue_only" class="font-medium text-slate-700">Show Overdue</label>
                        <p id="overdue_only-description" class="text-slate-500">
                            Show only overdue inspections.
                        </p>
                    </div>
                </div>
            </fieldset>
        </div>

        <div class="w-full lg:w-[12.5%] p-2">
          <p class="text-sm text-slate-600">Risk</p>
          <v-select
            class="style-chooser"
            multiple
            v-model="filter.trees_risk_id"
            :options="[
              { type: 'NA', id: 0 },
              { type: 'Low', id: 1 },
              { type: 'Moderate', id: 2 },
              { type: 'High', id: 3 },
              { type: 'Extreme', id: 4 },
            ]"
            label="type"
            :reduce="(type) => type.id"
          />
        </div>
        <div class="w-full lg:w-[12.5%] p-2">
          <p class="text-sm text-slate-600">Tree Type</p>
          <v-select
            class="style-chooser"
            multiple
            v-model="filter.park_tree"
            :options="[
              { type: 'Street Trees', id: false },
              { type: 'Park Trees', id: true },
            ]"
            label="type"
            :reduce="(type) => type.id"
          />
        </div>

        <div class="w-full p-2 lg:w-1/6">
          <p class="text-sm text-slate-600">Suburb</p>
          <v-select
            class="style-chooser"
            multiple
            v-model="filter.suburb"
            :options="suburbs"
            label="suburb"
            :reduce="(type) => type.suburb"
          />
        </div>

        <div class="w-full p-2 lg:w-1/4">
          <p class="text-sm text-slate-600">Tags</p>
          <v-select
            class="style-chooser"
            multiple
            v-model="filter.trees_tag_id"
            :options="attachments.tags"
            label="name"
            :reduce="(settings) => settings.id"
          />
        </div>
      </div>
    </div>

    <div class="flex justify-center w-full pb-4">
      <button
        @click.prevent="applyFilter()"
        class="p-2 px-6 mt-6 font-semibold text-blue-100 bg-blue-500 rounded-lg hover:bg-blue-600 text-md"
        :class="loading ? 'cursor-not-allowed opacity-50' : ''"
      >
        Apply Filter
      </button>
      <button
        @click.prevent="clearFilter()"
        class="p-2 px-6 mt-6 ml-6 font-semibold text-blue-700 rounded-lg cursor-pointer bg-slate-100 hover:text-blue-500 text-md"
        :class="loading ? 'cursor-not-allowed opacity-50' : ''"
      >
        Clear Filter
      </button>
    </div>

    <div  class="flex justify-between w-full pt-4">
      <p v-if="loading" class="my-3 text-3xl font-semibold text-slate-600">
        Loading...
      </p>
      <p v-if="pagination && !loading" class="my-3 text-3xl font-semibold text-slate-600">
        {{ pagination.total }} Results
      </p>

      <!-- Map controls -->
        <div class="flex justify-end flex-1 py-4">
          <div  v-if="showMap" class="flex ml-12 mr-auto">
            <div class="ml-2">
              <button
                  @click.prevent="enablePolygonDraw()"
                  class="p-3 mr-2 font-medium text-blue-700 rounded-lg cursor-pointer text-md hover:text-blue-500 bg-slate-100 hover:bg-slate-200"
              >
                  Draw Area
              </button>
            </div>
            <div v-if="zones && zones.length" class="ml-2">
                <button
                    ref="actions"
                    @click.prevent="toggleShowAreaMenu()"
                    class="p-3 font-semibold rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600"
                >
                    Choose Area
                </button>
                <div
                    v-if="isAreaMenuOpen"
                    class="relative right-0 w-full h-0"
                    role="menu"
                    aria-orientation="vertical"
                    aria-labelledby="menu-button"
                    tabindex="-1"
                >
                    <div
                        class="absolute right-0 z-20 w-56 py-1 mt-1 overflow-y-auto bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                        style="max-height: 75vh"
                    >
                        <div
                            v-for="z in zones"
                            :key="z.id"
                            @click="handleSelectArea(z.polygon)"
                            class="block px-4 py-2 text-sm cursor-pointer hover:bg-slate-100 text-slate-700"
                            role="menuitem"
                            tabindex="-1"
                            id="menu-item-0"
                        >
                            {{ z.name }}
                        </div>
                    </div>
                </div>
            </div>
            <button
                @click.prevent="clearArea()"
                class="my-1 ml-5 font-medium text-blue-700 cursor-pointer text-md hover:text-blue-500"
            >
                Remove Area(s)
            </button>
          </div>
          <div class="item-end">

            <button @click="showMap = !showMap"
              class="p-3 mr-2 font-medium text-blue-700 rounded-lg cursor-pointer text-md hover:text-blue-500 bg-slate-100 hover:bg-slate-200">
              Toggle Map
            </button>
            
            <Tooltip
              text="Draw an area on the map and click Apply Filter before creating a Project."
              position="top"
              :enabled="!polygonWkt">
              <button
                @click.prevent="showProject = true"
                :disabled="!polygonWkt"
                :class="[
                  'p-3 mr-4 font-medium text-blue-700 rounded-lg text-md bg-slate-100',
                  polygonWkt ? 'cursor-pointer hover:text-blue-500 hover:bg-slate-200' : 'opacity-50 cursor-not-allowed'
                ]"
              >
                Create Project
              </button>
            </Tooltip>
          </div>
      </div>      

        

        

        <div v-if="false">
          <div
            v-if="
              permissions &&
              permissions.admin_access &&
              pagination.total < 10001
            "
            ref="actions"
            class="pl-4 border-l-2"
          >
            <button
              @click.prevent="openActionsMenu()"
              class="p-3 mr-2 font-semibold rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600"
            >
              Actions
            </button>

            <div
              v-if="openActions"
              class="relative left-0 flex justify-end h-0"
              role="menu"
              aria-orientation="vertical"
              aria-labelledby="menu-button"
              tabindex="-1"
            >
              <div
                class="absolute z-10 w-auto py-1 mt-1 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                role="none "
              >
                <div
                  @click="
                    openActions = false;
                    showPostponeDates = true;
                  "
                  class="block px-4 py-2 text-sm cursor-pointer whitespace-nowrap hover:bg-slate-100 text-slate-700"
                  role="menuitem"
                  tabindex="-1"
                  id="menu-item-0"
                >
                  Postpone Filtered Inspection Dates
                </div>
              </div>
            </div>
          </div>
        </div>
    </div>

    <transition name="slide-down">
        <div class="w-full">
          <div :class="showMap ? 'visible map-height mb-12' : 'invisible h-0'">
              <div id="map" class="pb-6" style="height: 75vh">
                  <address-search :toggleMap="baseMapSwitch" :map="map" class="relative w-full"></address-search>
              </div>
              <div id="popup" class="ol-popup">
                  <a href="#" id="popup-closer" class="ol-popup-closer"></a>
                  <div id="popup-content" class="text-slate-600"></div>
              </div>
          </div>
        </div>
    </transition>

    <div class="flex flex-col">
      <div class="py-2 mt-0 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
        <div
          class="inline-block min-w-full overflow-hidden align-middle shadow sm:rounded-lg"
        >
          <table class="min-w-full">
            <thead>
              <tr>
                <th
                  class="px-6 py-3 text-xs font-medium leading-4 tracking-wider text-left uppercase border-b border-slate-200 bg-slate-100 text-slate-500"
                >
                  Tree ID
                </th>
                <th
                  class="px-6 py-3 text-xs font-medium leading-4 tracking-wider text-left uppercase border-b border-slate-200 bg-slate-100 text-slate-500"
                >
                  Inspection Due
                </th>
                <th
                  class="px-6 py-3 text-xs font-medium leading-4 tracking-wider text-left uppercase border-b border-slate-200 bg-slate-100 text-slate-500"
                >
                  Species
                </th>
                <th
                  class="px-6 py-3 text-xs font-medium leading-4 tracking-wider text-left uppercase border-b border-slate-200 bg-slate-100 text-slate-500"
                >
                  Risk
                </th>
                <th
                  class="px-6 py-3 text-xs font-medium leading-4 tracking-wider text-right uppercase border-b border-slate-200 bg-slate-100 text-slate-500"
                >
                  Age/ULE
                </th>
              </tr>
            </thead>
            <tbody class="bg-white">
              <table-row-data-loader :loading="loading" :colspan="6" loaderTextClass="text-blue-500" />
              <tr v-if="!loading" v-for="tree in pagination.data" :key="tree.id">
                <td
                  class="px-6 py-4 border-b whitespace-nowrap border-slate-200"
                >
                  <div class="flex items-center">
                    <div class="">
                      <div class="text-sm font-medium leading-5 text-slate-900">
                        Tree ID:
                        <a
                          :href="'/map?treeId=' + tree.id"
                          class="text-blue-600"
                          >{{ tree.id }}</a
                        >
                      </div>
                      <div class="text-sm leading-5 text-slate-500">
                        {{ tree.asset_id ? "Asset ID: " + tree.asset_id : "" }}
                      </div>
                    </div>
                  </div>
                </td>
                <td
                  class="px-6 py-4 border-b whitespace-nowrap border-slate-200"
                >
                  <div class="text-sm leading-5 text-slate-900">
                    <span class="pb-1 pr-1 font-semibold">{{
                        justDate(tree.next_inspect_date)
                      }}</span>
                    <span
                      :class="
                        overdue(tree.next_inspect_date)
                          ? 'bg-red-200 text-red-700 font-semibold'
                          : 'bg-orange-200 text-orange-700'
                      "
                      class="px-3 py-1 text-xs rounded-full"
                      >{{ getStatus(tree.next_inspect_date) }}
                    </span>
                  </div>
                  <div class="text-sm leading-5 text-slate-500">
                    <span class="italic">{{
                      tree.scheduled_inspection ? "Custom Schedule" : ""
                    }}</span>
                  </div>
                </td>
                <td
                  class="px-6 py-4 border-b whitespace-nowrap border-slate-200"
                >
                  <div class="text-sm leading-5 text-slate-900">
                    {{ tree.species ? tree.species.genus : "NA" }} ({{
                      tree.species ? tree.species.common_name : "NA"
                    }})
                  </div>
                  <div class="text-sm leading-5 text-slate-500">
                    <span class="italic">{{
                      tree.species ? tree.species.species : "NA"
                    }}</span>
                    {{ tree.species ? tree.species.family : "NA" }}
                  </div>
                </td>
                <td
                  class="px-6 py-4 text-sm leading-5 border-b whitespace-nowrap border-slate-200 text-slate-500"
                >
                  <span
                    v-if="tree.trees_risk_id === 1"
                    class="px-3 py-1 text-xs text-white bg-green-500 rounded-full"
                    >Low Risk</span
                  >
                  <span
                    v-if="tree.trees_risk_id === 2"
                    class="px-3 py-1 text-xs text-white bg-orange-500 rounded-full"
                    >Moderate Risk</span
                  >
                  <span
                    v-if="tree.trees_risk_id === 3"
                    class="px-3 py-1 text-xs text-white bg-red-500 rounded-full"
                    >High Risk</span
                  >
                  <span
                    v-if="tree.trees_risk_id === 4"
                    class="px-3 py-1 text-xs text-white bg-red-700 rounded-full"
                    >Extreme Risk</span
                  >
                </td>
                <td
                  class="px-6 py-4 text-sm leading-5 text-right border-b whitespace-nowrap border-slate-200"
                >
                  <div class="text-sm leading-5 text-slate-900">
                    {{ tree.age ? tree.age.name : "NA" }}
                  </div>
                  <div class="text-sm leading-5 text-slate-500">
                    {{ tree.ule ? tree.ule.name : "NA" }}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div
      v-if="pagination && !loading"
      class="flex items-center justify-between px-4 py-3 bg-white border-t border-slate-200 sm:px-6"
    >
      <div class="flex justify-between flex-1 sm:hidden">
        <a
          href="#"
          @click.stop="changePageUrl(pagination.prev_page_url)"
          class="relative inline-flex items-center px-4 py-2 text-sm font-medium leading-5 transition duration-150 ease-in-out bg-white border rounded-md border-slate-300 text-slate-700 hover:text-slate-500 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 active:bg-slate-100 active:text-slate-700"
          >Previous</a
        >
        <a
          href="#"
          @click.stop="changePageUrl(pagination.next_page_url)"
          class="relative inline-flex items-center px-4 py-2 ml-3 text-sm font-medium leading-5 transition duration-150 ease-in-out bg-white border rounded-md border-slate-300 text-slate-700 hover:text-slate-500 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 active:bg-slate-100 active:text-slate-700"
          >Next</a
        >
      </div>
      <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div>
          <p class="text-sm leading-5 text-slate-700">
            Showing
            <span class="font-medium">{{ pagination.current_page }}</span>
            to
            <span class="font-medium">{{ pagination.last_page }}</span>
            of
            <span class="font-medium">{{ pagination.total }}</span>
            results
          </p>
        </div>
        <div>
          <span class="relative z-0 inline-flex shadow-sm">
            <button
              type="button"
              @click.stop="changePageUrl(pagination.prev_page_url)"
              class="relative inline-flex items-center px-2 py-2 text-sm font-medium leading-5 transition duration-150 ease-in-out bg-white border rounded-l-md border-slate-300 text-slate-500 hover:text-slate-400 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-slate-100 active:text-slate-500"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                  clip-rule="evenodd"
                />
              </svg>
            </button>

            <button
              v-for="page in pages"
              :key="page"
              @click.stop="changePage(page)"
              type="button"
              :class="
                page === pagination.current_page
                  ? 'text-blue-700 hover:text-blue-500 bg-slate-100'
                  : 'bg-white text-slate-700 hover:text-slate-500'
              "
              class="relative inline-flex items-center px-4 py-2 -ml-px text-sm font-medium leading-5 transition duration-150 ease-in-out border border-slate-300 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-slate-100 active:text-slate-700"
            >
              {{ page }}
            </button>

            <span
              class="relative inline-flex items-center px-4 py-2 -ml-px text-sm font-medium leading-5 bg-white border border-slate-300 text-slate-700"
              >...</span
            >

            <button
              type="button"
              @click.stop="changePageUrl(pagination.last_page_url)"
              :class="
                pagination.last_page === pagination.current_page
                  ? 'text-blue-700 hover:text-blue-500 bg-slate-100'
                  : 'bg-white text-slate-700 hover:text-slate-500'
              "
              class="relative inline-flex items-center px-4 py-2 -ml-px text-sm font-medium leading-5 transition duration-150 ease-in-out bg-white border border-slate-300 text-slate-700 hover:text-slate-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-slate-100 active:text-slate-700"
            >
              {{ pagination.last_page }}
            </button>

            <button
              type="button"
              @click.stop="changePageUrl(pagination.next_page_url)"
              class="relative inline-flex items-center px-2 py-2 -ml-px text-sm font-medium leading-5 transition duration-150 ease-in-out bg-white border rounded-r-md border-slate-300 text-slate-500 hover:text-slate-400 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-slate-100 active:text-slate-500"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clip-rule="evenodd"
                />
              </svg>
            </button>
          </span>
        </div>
      </div>
    </div>

    <div
      v-if="showPostponeDates && pagination.total < 10001"
      class="fixed inset-0 z-10 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      style="z-index: 1000"
      aria-modal="true"
    >
      <div
        class="flex items-center justify-center min-h-screen text-center sm:block sm:p-0"
      >
        <div
          class="fixed inset-0 bg-slate-500"
          aria-hidden="true"
          style="opacity: 0.6"
        ></div>

        <span
          class="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
          >&#8203;</span
        >

        <div
          v-if="postponeLoading"
          class="relative inline-block py-12 overflow-hidden text-left transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
        >
          <div class="flex justify-center">
            <img src="/loading.gif" width="64px" height="64px" />
          </div>
          <div class="flex justify-center">
            <span class="text-lg text-slate-800">Updating... Please wait</span>
          </div>
        </div>
        <div
          v-else
          class="relative inline-block overflow-hidden text-left transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
        >
          <div class="px-4 py-2 bg-white sm:pb-2">
            <div class="py-2 bg-white">
              <div
                class="flex flex-wrap items-end justify-between sm:flex-nowrap"
              >
                <div class="">
                  <h3 class="text-lg font-medium leading-6 text-slate-900">
                    Postpone Scheduled Inspection Dates ({{ pagination.total }}
                    Filtered Trees)
                  </h3>
                  <p class="py-3 text-sm text-slate-600">
                    Randomly spread the these trees
                    <b>Next Scheduled Inspection Dates</b> over
                    <b>{{ postponeDays }}</b> days starting from
                    {{
                      postponeFromDateEnabled
                        ? postponeFromDate
                        : "the tree's current next scheduled inspection date."
                    }}
                  </p>

                  <div class="flex items-end pt-3 pb-2 border-t">
                    <div class="text-slate-700">
                      <label class="text-sm font-medium text-slate-700"
                        >Days to Spread Dates</label
                      >
                      <input
                        id="postponeDays"
                        name="postponeDays"
                        type="number"
                        min="1"
                        v-model="postponeDays"
                        class="block w-full px-3 py-2 rounded-lg shadow-sm appearance-none bg-slate-100 placeholder-slate-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                  </div>
                  <div class="flex items-end py-3">
                    <div class="relative flex items-start">
                      <div class="flex items-center h-5">
                        <input
                          v-model="postponeFromDateEnabled"
                          id="enable"
                          aria-describedby="enable-description"
                          name="enable"
                          type="checkbox"
                          class="w-5 h-5 mt-2 rounded focus:ring-blue-500 text-slate-700 border-slate-300"
                        />
                      </div>
                      <div class="ml-3 text-sm">
                        <label
                          htmlFor="enable"
                          class="font-medium text-slate-700"
                        >
                          Custom From Date
                        </label>
                        <p id="enable-description" class="pr-4 text-slate-500">
                          If checked, the scheduled inspections will spread from
                          the date selected below.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div class="flex items-end pb-3 border-b">
                    <div class="text-slate-700">
                      <label class="text-sm font-medium text-slate-700"
                        >Starting From Date</label
                      >
                      <input
                        :disabled="!postponeFromDateEnabled"
                        id="postponeFromDate"
                        name="postponeFromDate"
                        type="date"
                        v-model="postponeFromDate"
                        :class="!postponeFromDateEnabled ? 'opacity-50' : ''"
                        class="block w-full px-3 py-2 rounded-lg shadow-sm appearance-none bg-slate-100 placeholder-slate-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                  </div>
                  <p v-if="postponeMsg" class="py-1 italic text-red-500 tex-sm">
                    {{ postponeMsg }}
                  </p>

                  <p class="py-3 text-sm text-slate-500">
                    NOTE: Trees with Custom Schedules will not be included.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div
            class="px-4 py-3 bg-slate-100 sm:px-6 sm:flex sm:flex-row-reverse"
          >
            <button
              @click="updatesInspections()"
              type="button"
              class="inline-flex justify-center w-full px-4 py-2 mb-2 text-base font-medium text-white bg-blue-500 rounded-lg shadow-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm md:mb-0"
            >
              Update
            </button>
            <button
              @click="
                showPostponeDates = false;
                postponeMsg = null;
              "
              type="button"
              class="inline-flex justify-center w-full px-4 py-2 text-base font-medium bg-white rounded-lg shadow-sm text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>

    <create-project
      v-if="showProject && pagination"
      :total="pagination.total"
      :url="queryString(false)"
      :users="null"
      :defaults="null"
      :closeProject="closeProject"
      :saveProject="saveProject"
      :project="project"
      :filter="filter"
    />
  </div>
</template>

<style>
.style-chooser .vs__selected {
  background-color: #edf2f7;
  border: 1px solid #dbebfb;
  color: #2b6cb0;
  line-height: 1.7;
}

.style-chooser .vs__search {
  line-height: 1.7;
}

.map-height {
  height: 70vh;
}

.style-chooser .vs__search::placeholder,
.style-chooser .vs__dropdown-toggle,
.style-chooser .vs__dropdown-menu {
  background: #ffffff;
  border: 1px solid #cbd5e0;
  color: #394066;
}

.style-chooser .vs__clear,
.style-chooser .vs__open-indicator {
  fill: #394066;
}
</style>
